import Button from '@axa-japan/design-system-react/Button';
import DatePicker, { CalendarDateTime } from '@axa-japan/design-system-react/DatePickerField';
import Heading from '@axa-japan/design-system-react/Heading';
import SelectField from '@axa-japan/design-system-react/SelectField';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import { AxiosError } from 'axios';
import { ChangeEventHandler, FC, FormEventHandler, useState } from 'react';
import apiClient from '../libs/apiClient';

type LogsForm = {
  from: CalendarDateTime;
  to: CalendarDateTime;
};

const HoursOptions = Array.from(Array(24).keys()).map((i) => ({ text: i.toString().padStart(2, '0'), value: String(i) }));
const MinutesOptions = Array.from(Array(60).keys()).map((i) => ({ text: i.toString().padStart(2, '0'), value: String(i) }));

const convertDateToString = (date: CalendarDateTime) => {
  const { year, month, day, hour, minute, second } = date;
  return [year, month, day, hour, minute, second].map((d, i) => String(d).padStart(i > 0 ? 2 : 4, '0')).join('');
};

const LogsPage: FC = () => {
  const today = new Date();
  const defaultDate = new CalendarDateTime(today.getFullYear(), today.getMonth() + 1, today.getDate(), 0, 0, 0);
  const [form, setForm] = useState<LogsForm>({
    from: defaultDate,
    to: defaultDate,
  });
  const [dialogMessage, setDialogMessage] = useState<string>('');

  const handleDateChange: (key: keyof LogsForm) => ChangeEventHandler<HTMLInputElement> =
    (key) =>
    ({ target }) => {
      const [year, month, day] = target.value.split('/').map((v) => Number(v));
      const date = form[key];
      setForm({ ...form, [key]: new CalendarDateTime(year, month, day, date.hour, date.minute, date.second) });
    };

  const handleTimeChange = (key: keyof LogsForm, time: 'hour' | 'minute') => (value: string) => {
    const date = form[key];
    setForm({
      ...form,
      [key]: new CalendarDateTime(
        date.year,
        date.month,
        date.day,
        time === 'hour' ? Number(value) : date.hour,
        time === 'minute' ? Number(value) : date.minute,
        date.second,
      ),
    });
  };

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.get('/logs', {
        params: {
          from: convertDateToString(form.from),
          to: convertDateToString(form.to),
        },
        headers: { Accept: 'application/zip' },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: String(response.headers['content-type']) || 'application/zip' });
      let filename = 'logs.zip';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) filename = contentDisposition.match(/filename="(.+)"/)[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status === 400) setDialogMessage('入力した値は正しくありません。');
        if (e.status === 401) setDialogMessage('ユーザーが操作ログをダウンロードする権限がありません。');
        return;
      }
      setDialogMessage('不明なエラーが発生しました。ITへの連絡をください。');
    }
  };

  return (
    <>
      <StatusModal
        open={Boolean(dialogMessage)}
        setOpen={(open) => !open && setDialogMessage('')}
        title="エラー"
        content={dialogMessage}
        buttons={[{ text: '閉じる' }]}
      />
      <div className="flex flex-col gap-5 mt-6">
        <Heading as="h3" size="2xl" align="center">
          操作ログ
        </Heading>
        <Text align="center">抽出したい期間を指定してください</Text>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 justify-center m-auto">
          <div className="flex gap-3">
            <div>
              <DatePicker label="開始日" value={form.from} onChange={handleDateChange('from')} />
            </div>
            <div>
              <SelectField label="時" value={String(form.from.hour)} onValueChange={handleTimeChange('from', 'hour')} options={HoursOptions} />
            </div>
            <div>
              <SelectField label="分" value={String(form.from.minute)} onValueChange={handleTimeChange('from', 'minute')} options={MinutesOptions} />
            </div>
          </div>
          <div className="flex gap-3">
            <div>
              <DatePicker label="終了日" value={form.to} name="to" onChange={handleDateChange('to')} />
            </div>
            <div>
              <SelectField label="時" value={String(form.to.hour)} onValueChange={handleTimeChange('to', 'hour')} options={HoursOptions} />
            </div>
            <div>
              <SelectField label="分" value={String(form.to.minute)} onValueChange={handleTimeChange('to', 'minute')} options={MinutesOptions} />
            </div>
          </div>
          <div className="mt-3">
            <Button type="submit" fullWidth>
              ダウンロード
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default LogsPage;
